import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { partyList, getBillingsByParty } from "../../../Services/Services";
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
  CalendarToday,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { printBillReport } from "../Bill/billPDFGenerator";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);
const getInitials = (s) =>
  s
    ? s
        .split(" ")
        .filter((w) => w)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";
const getRefNo = (r) =>
  `${r.branch_name === "RAIPUR" ? "SRE" : r.branch_name === "BILASPUR" ? "JHT" : "MBR"}/${r.billing_id}/${getInitials(r.party_name)}`;

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({ tip, icon, color, isPrint, onClick }) => {
  const c = isPrint ? T.warning : color || T.primary;
  const bg = isPrint ? T.warningBg : `${c}14`;
  const bd = isPrint ? `${T.warning}30` : `${c}30`;
  const hBg = isPrint ? "#fef3c7" : `${c}28`;

  return (
    <Tooltip title={tip} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          bgcolor: bg,
          color: c,
          border: `1px solid ${bd}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
  </Paper>
);

// ── LabelValue ─────────────────────────────────────────────────────
const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

// ── MobileBillCard ─────────────────────────────────────────────────
const MobileBillCard = ({ row, idx, expanded, onToggle, onPrint }) => {
  const isComplete = row.is_complete;
  const isNegative = row.rem_amount < 0;
  const accentColor = isComplete ? T.success : isNegative ? T.red : T.warning;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Card Header */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        {/* Icon column */}
        <Box
          sx={{
            width: 58,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <ReceiptIcon
            sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.46rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        {/* Main info */}
        <Box
          sx={{
            flex: 1,
            px: 1.2,
            py: 0.75,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.15,
            }}
          >
            {getRefNo(row)}
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          <Chip
            label={isComplete ? "Settled" : "Pending"}
            size="small"
            sx={{
              ...sq,
              alignSelf: "flex-start",
              bgcolor: `${accentColor}14`,
              color: accentColor,
              fontSize: "0.48rem",
              height: 15,
              border: `1px solid ${accentColor}30`,
            }}
          />
        </Box>

        {/* Amount + toggle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1,
            py: 0.75,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
            >
              Balance
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: isNegative ? T.red : isComplete ? T.success : T.warning,
              }}
            >
              {formatCurrency(row.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 22,
              fontSize: "0.55rem",
              px: 0.7,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded detail */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            {/* Amounts */}
            <Box
              sx={{
                p: 0.75,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💳 Amounts
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Total", formatCurrency(row.total_amount), null, 6],
                  [
                    `TDS ${row.tds_percent ? `(${row.tds_percent}%)` : ""}`,
                    formatCurrency(row.tds_amount),
                    T.red,
                    6,
                  ],
                  ["Deduction", formatCurrency(row.deduction_amount), T.red, 6],
                  [
                    "Net Payable",
                    formatCurrency(row.net_payable),
                    T.primary,
                    6,
                  ],
                  ["Paid", formatCurrency(row.paid_amount), T.success, 6],
                  [
                    "Balance",
                    formatCurrency(row.rem_amount),
                    isNegative ? T.red : T.warning,
                    6,
                  ],
                ].map(([l, v, c, xs]) => (
                  <Grid item xs={xs} key={l}>
                    <LabelValue label={l} value={v} color={c} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Follow / Remarks */}
            {(row.follow_date || row.remarks || row.payment_remarks) && (
              <Box
                sx={{
                  p: 0.75,
                  bgcolor: T.white,
                  border: `1px solid ${T.border}`,
                  ...sq,
                }}
              >
                {row.follow_date && (
                  <LabelValue
                    label="Follow Date"
                    value={formatDate(row.follow_date)}
                  />
                )}
                {row.remarks && (
                  <Box sx={{ mt: 0.5 }}>
                    <LabelValue label="Remark" value={row.remarks} />
                  </Box>
                )}
                {row.payment_remarks && (
                  <Box sx={{ mt: 0.5 }}>
                    <LabelValue
                      label="Pay Remark"
                      value={row.payment_remarks}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Action */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                pt: 0.75,
                borderTop: `1px dashed ${T.border}`,
                justifyContent: "flex-end",
              }}
            >
              <Button
                size="small"
                startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
                onClick={() => onPrint(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.warning}14`,
                  color: T.warning,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.warning}28`, boxShadow: "none" },
                }}
              >
                Print
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function PartyBillSummaryTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [partyData, setPartyData] = useState([]);
  const [billData, setBillData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [selectedPartyName, setSelectedPartyName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [filters, setFilters] = useState({
    lrNumber: "",
    billDate: "",
  });

  const debouncedLrNumber = useDebounce(filters.lrNumber, 500);
  const debouncedBillDate = useDebounce(filters.billDate, 500);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  // ADD
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setPartyData(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setPartyData([]);
      }
    };
    fetchParties();
  }, []);

  const fetchBillList = useCallback(async (partyId, pn, af) => {
    setLoading(true);
    try {
      const d = await getBillingsByParty(partyId, pn, af);
      if (pn === 1) {
        setBillData(d.results || []);
        setPage(2);
      } else if (d?.results?.length) {
        setBillData((p) => [...p, ...d.results]);
        setPage(pn + 1);
      }
      setHasMore(Boolean(d?.next));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedPartyId) return;
    const activeFilters = {
      lrNumber: debouncedLrNumber,
      billDate: debouncedBillDate,
    };
    setBillData([]);
    setPage(1);
    setHasMore(true);
    fetchBillList(selectedPartyId, 1, activeFilters);
    setExpandedCardId(null);
  }, [selectedPartyId, debouncedLrNumber, debouncedBillDate, fetchBillList]);

  const fetchMoreData = useCallback(() => {
    if (!selectedPartyId || !hasMore || loading) return;
    fetchBillList(selectedPartyId, page, {
      lrNumber: debouncedLrNumber,
      billDate: debouncedBillDate,
    });
  }, [
    selectedPartyId,
    hasMore,
    loading,
    page,
    debouncedLrNumber,
    debouncedBillDate,
    fetchBillList,
  ]);

  const handlePartyChange = (pid, pn) => {
    if (!pid) {
      setBillData([]);
      setSelectedPartyId(null);
      setSelectedPartyName(null);
      setFilters({ lrNumber: "", billDate: "" });
      return;
    }
    setBillData([]);
    setPage(1);
    setHasMore(true);
    setSelectedPartyId(pid);
    setSelectedPartyName(pn);
    setExpandedCardId(null);
  };

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const clearFilters = () => {
    setFilters({ lrNumber: "", billDate: "" });
  };

  const handlePrint = (row) =>
    printBillReport([row], {
      total_amount: row.total_amount,
      paid_amount: row.paid_amount,
      rem_amount: row.rem_amount,
    });

  const totals = {
    total_amount: billData.reduce((s, c) => s + (+c.total_amount || 0), 0),
    tds_amount: billData.reduce((s, c) => s + (+c.tds_amount || 0), 0),
    deduction_amount: billData.reduce(
      (s, c) => s + (+c.deduction_amount || 0),
      0,
    ),
    net_payable: billData.reduce((s, c) => s + (+c.net_payable || 0), 0),
    paid_amount: billData.reduce((s, c) => s + (+c.paid_amount || 0), 0),
    rem_amount: billData.reduce((s, c) => s + (+c.rem_amount || 0), 0),
  };

  const EmptyState = ({ message }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        border: `1.5px dashed ${T.border}`,
        bgcolor: T.primaryBg,
        ...sq,
      }}
    >
      <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}>
        {message}
      </Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stat Cards ── */}
      <Box sx={{ mt: 2, mb: 1.5, width: "100%" }}>
        <Grid
          container
          spacing={0.75}
          sx={{
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": { px: "3px !important", py: "3px !important" },
          }}
        >
          {[
            {
              title: "Total Amount",
              value: formatCurrency(totals.total_amount),
              color: T.primary,
            },
            {
              title: "Net Payable",
              value: formatCurrency(totals.net_payable),
              color: T.warning,
            },
            {
              title: "Paid Amount",
              value: formatCurrency(totals.paid_amount),
              color: T.success,
            },
            {
              title: "Remaining",
              value: formatCurrency(totals.rem_amount),
              color: T.red,
            },
          ].map((s) => (
            <Grid item xs={6} sm={3} key={s.title}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Party Select */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={partyData}
              getOptionLabel={(o) => o?.name || ""}
              onChange={(_, s) =>
                handlePartyChange(s?.party_id || null, s?.name || null)
              }
              isOptionEqualToValue={(o, v) => o?.party_id === v?.party_id}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder="Select Party"
                  InputProps={{
                    ...p.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ fontSize: 14, color: T.primary }}
                          />
                        </InputAdornment>
                        {p.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* LR Number */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="LR No."
              value={filters.lrNumber}
              onChange={(e) => handleFilterChange("lrNumber", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 14, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Bill Date */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Bill Date"
              InputLabelProps={{ shrink: true }}
              value={filters.billDate}
              onChange={(e) => handleFilterChange("billDate", e.target.value)}
              sx={filterInputSx}
            />
          </Grid>

          {/* Selected party chip */}
          {selectedPartyId && (
            <Grid item xs="auto">
              <Chip
                label={selectedPartyName}
                size="small"
                onDelete={() => handlePartyChange(null, null)}
                sx={{
                  ...sq,
                  bgcolor: T.primaryBg,
                  color: T.primary,
                  fontSize: "0.65rem",
                  border: `1px solid ${T.border}`,
                }}
              />
            </Grid>
          )}

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && !billData.length ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : !billData.length ? (
            <EmptyState
              message={
                !selectedPartyId
                  ? "Select a party to view bills"
                  : "No bills found"
              }
            />
          ) : (
            <Box
              id="party-bill-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={billData.length}
                next={fetchMoreData}
                hasMore={hasMore && !!selectedPartyId}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="party-bill-mobile"
              >
                {billData.map((r, i) => (
                  <MobileBillCard
                    key={r.billing_id}
                    row={r}
                    idx={i}
                    expanded={expandedCardId === r.billing_id}
                    onToggle={() => handleCardToggle(r.billing_id)}
                    onPrint={handlePrint}
                  />
                ))}
              </InfiniteScroll>

              {/* Mobile Summary Footer */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.primaryLight,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Grand Summary
                </Typography>
                <Grid container spacing={0.75}>
                  {[
                    ["Total", formatCurrency(totals.total_amount), T.primary],
                    [
                      "Net Payable",
                      formatCurrency(totals.net_payable),
                      T.warning,
                    ],
                    ["Paid", formatCurrency(totals.paid_amount), T.success],
                    ["Remaining", formatCurrency(totals.rem_amount), T.red],
                  ].map(([l, v, c]) => (
                    <Grid item xs={6} key={l}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {l}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                      >
                        {v}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <Box
            id="party-bill-container"
            sx={{ maxHeight: "calc(100vh - 380px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={billData.length}
              next={fetchMoreData}
              hasMore={hasMore && !!selectedPartyId}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="party-bill-container"
            >
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1420,
                    borderCollapse: "collapse",
                    "& td, & th": {
                      py: 0,
                      px: 0.75,
                      fontSize: "0.75rem",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    },
                    "& td": {
                      borderBottom: `1px solid #ccc`,
                      height: 26,
                    },
                    "& th": {
                      fontWeight: 700,
                      color: T.headerText,
                      bgcolor: `${T.headerBg} !important`,
                      py: 0.2,
                      height: 28,
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                      borderBottom: `1px solid #ccc`,
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        { l: "#", w: 35, sticky: true, left: 0 },
                        { l: "Ref No", w: 130, sticky: true, left: 35 },
                        { l: "Bill Date", w: 95 },
                        { l: "Total Amt", w: 100 },
                        { l: "TDS Amt", w: 100 },
                        { l: "Deduction", w: 110 },
                        { l: "Net Payable", w: 110, highlight: true },
                        { l: "Rec Amt", w: 100 },
                        { l: "Bal Amt", w: 100, highlight: true },
                        { l: "Follow Date", w: 95 },
                        { l: "Follow Remark", w: 130 },
                        { l: "Pay Remark", w: 130 },
                        { l: "Actions", w: 70, stickyRight: true },
                      ].map(
                        ({ l, w, sticky, left, highlight, stickyRight }) => (
                          <TableCell
                            key={l}
                            sx={{
                              minWidth: w,
                              bgcolor: `${T.headerBg} !important`,
                              color: highlight
                                ? `${T.success} !important`
                                : `${T.headerText} !important`,
                              ...(sticky && {
                                position: "sticky",
                                left,
                                zIndex: 12,
                              }),
                              ...(stickyRight && {
                                position: "sticky",
                                right: 0,
                                zIndex: 12,
                              }),
                            }}
                          >
                            {l}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading && !billData.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={13}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <CircularProgress
                            sx={{ color: T.primary }}
                            size={32}
                          />
                        </TableCell>
                      </TableRow>
                    ) : !billData.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={13}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <ReceiptIcon
                            sx={{
                              fontSize: 40,
                              color: T.border,
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography
                            sx={{
                              color: T.textSub,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            {!selectedPartyId
                              ? "Select a party to view bills"
                              : "No records found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {billData.map((row, idx) => {
                          const isNegative = row.rem_amount < 0;
                          const isComplete = row.is_complete;
                          const isEven = idx % 2 === 0;
                          const rowBg = isEven ? T.baseRow : T.altRow;
                          const sc = {
                            bgcolor: `${rowBg} !important`,
                            zIndex: 5,
                          };

                          return (
                            <TableRow
                              key={row.billing_id}
                              sx={{
                                "& > td": {
                                  bgcolor: rowBg,
                                  transition: "background-color 0.12s",
                                },
                                "&:hover > td": {
                                  bgcolor: `${T.rowHover} !important`,
                                },
                              }}
                            >
                              {/* # */}
                              <TableCell
                                sx={{ position: "sticky", left: 0, ...sc }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    color: T.textMuted,
                                  }}
                                >
                                  {idx + 1}
                                </Typography>
                              </TableCell>

                              {/* Ref No */}
                              <TableCell
                                sx={{ position: "sticky", left: 35, ...sc }}
                              >
                                <Chip
                                  label={getRefNo(row)}
                                  size="small"
                                  sx={{
                                    ...sq,
                                    bgcolor: T.primaryBg,
                                    color: T.primary,
                                    fontSize: "0.6rem",
                                    height: 16,
                                    border: `1px solid ${T.border}`,
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              </TableCell>

                              {/* Bill Date */}
                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {formatDate(row.created_at)}
                              </TableCell>

                              {/* Total Amt */}
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                              >
                                {formatCurrency(row.total_amount)}
                              </TableCell>

                              {/* TDS Amt — NEW */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    color: T.red,
                                  }}
                                >
                                  {formatCurrency(row.tds_amount)}
                                </Typography>
                              </TableCell>

                              {/* Deduction Amt — NEW */}
                              <TableCell>
                                <Tooltip
                                  title={
                                    row.deduction_remarks ||
                                    "No deduction remarks"
                                  }
                                  arrow
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      color: T.red,
                                    }}
                                  >
                                    {formatCurrency(row.deduction_amount)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              {/* Net Payable — NEW (highlighted) */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: "0.78rem",
                                    color: T.primary,
                                  }}
                                >
                                  {formatCurrency(row.net_payable)}
                                </Typography>
                              </TableCell>

                              {/* Rec Amt */}
                              <TableCell
                                sx={{
                                  color: T.success,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {formatCurrency(row.paid_amount)}
                              </TableCell>

                              {/* Bal Amt */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    color: isNegative
                                      ? T.red
                                      : isComplete
                                        ? T.success
                                        : T.warning,
                                  }}
                                >
                                  {formatCurrency(row.rem_amount)}
                                </Typography>
                              </TableCell>

                              {/* Follow Date */}
                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {row.follow_date
                                  ? formatDate(row.follow_date)
                                  : "—"}
                              </TableCell>

                              {/* Follow Remark */}
                              <TableCell>
                                <Tooltip title={row.remarks || ""} arrow>
                                  <Typography
                                    sx={{
                                      maxWidth: 120,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {row.remarks || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              {/* Pay Remark */}
                              <TableCell>
                                <Tooltip
                                  title={row.payment_remarks || ""}
                                  arrow
                                >
                                  <Typography
                                    sx={{
                                      maxWidth: 120,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {row.payment_remarks || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              {/* Actions */}
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  ...sc,
                                  py: "2px !important",
                                }}
                              >
                                <ActionButton
                                  tip="Print"
                                  icon={<PrintIcon />}
                                  isPrint
                                  onClick={() => handlePrint(row)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* Totals Row */}
                        <TableRow
                          sx={{
                            position: "sticky",
                            bottom: 0,
                            zIndex: 20,
                            "& td": {
                              bgcolor: T.successBg,
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              py: 0.5,
                              borderTop: `2px solid ${T.primary}`,
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 22,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          >
                            —
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 35,
                              zIndex: 22,
                              color: T.primary,
                              fontWeight: 800,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          >
                            Totals
                          </TableCell>
                          <TableCell /> {/* Bill Date */}
                          <TableCell sx={{ fontWeight: 700 }}>
                            {formatCurrency(totals.total_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.red, fontWeight: 700 }}>
                            {formatCurrency(totals.tds_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.red, fontWeight: 700 }}>
                            {formatCurrency(totals.deduction_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.primary, fontWeight: 800 }}>
                            {formatCurrency(totals.net_payable)}
                          </TableCell>
                          <TableCell sx={{ color: T.success, fontWeight: 700 }}>
                            {formatCurrency(totals.paid_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.primary, fontWeight: 800 }}>
                            {formatCurrency(totals.rem_amount)}
                          </TableCell>
                          <TableCell colSpan={3} />{" "}
                          {/* Follow Date, Follow Remark, Pay Remark */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              zIndex: 22,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
